import http, { httpMultipart } from "./http";

export type ClientAccountStatus =
  | "For Activation"
  | "Active"
  | "On Hold"
  | "Grace Period"
  | "Offboarding"
  | "Offboarded"
  | "Reactivation Onboarding";
export type ClientRenewalStatus =
  | "Active"
  | "Upcoming Renewal"
  | "Due Soon"
  | "Renewed"
  | "Expired"
  | "Cancelled"
  | "Paused";

export interface CreateClientPayload {
  company_name: string;
  email: string;
  password: string;
  website_url?: string;
  activation_date?: string;
  onboarding_date?: string | null;
  subscription_start_date?: string | null;
  renewal_date?: string | null;
  renewal_start_date?: string | null;
  renewal_end_date?: string | null;
  payment_due_date?: string | null;
  date_renewed?: string | null;
  months_renewed?: number | null;
  renewal_status?: ClientRenewalStatus | null;
  renewal_note?: string | null;
  assigned_user_id?: string | null;
  assigned_member_ids?: string[];
  package_id: number;
  service_ids: number[];
  services?: string[];
  client_account_status?: ClientAccountStatus;
  offboarding_tasks_complete?: boolean;
}

export interface UpdateClientPayload {
  company_name?: string;
  email?: string | null;
  website_url?: string;
  password?: string;
  send_login_credentials?: boolean;
  login_access_code?: string;
  activation_date?: string;
  onboarding_date?: string | null;
  subscription_start_date?: string | null;
  renewal_date?: string | null;
  renewal_start_date?: string | null;
  renewal_end_date?: string | null;
  payment_due_date?: string | null;
  date_renewed?: string | null;
  months_renewed?: number | null;
  renewal_status?: ClientRenewalStatus | null;
  renewal_note?: string | null;
  assigned_user_id?: string | null;
  assigned_member_ids?: string[];
  package_id?: number;
  service_ids?: number[];
  client_account_status?: ClientAccountStatus;
  offboarding_tasks_complete?: boolean;
  dmp_plan_file?: File | null;
  activation_plan_type?: "standard" | "custom" | null;
  whatsapp_notifications_enabled?: boolean;
  whatsapp_number?: string | null;
  activation_custom_package_id?: number | null;
  activation_primary_goal?: string | null;
  activation_secondary_goal?: string | null;
  service_start_date?: string | null;
  service_end_date?: string | null;
  activation_custom_goals?: string[];
  activation_notes?: string | null;
}

export interface SyncDmpClientsResponse {
  message: string;
  meta: {
    received: number;
    synced: number;
    source: string;
  };
}

export interface DmpLifecycleAccount {
  id: string | number;
  dmp_lifecycle: {
    client_account_status: string;
    date_of_sale: string | null;
    onboarding_target_on: string | null;
    onboarding_workflow_complete: boolean;
    onboarding_completed_on: string | null;
    onboarding_pending_steps: string[];
    current_term: Record<string, any>;
    next_term: Record<string, any> | null;
    latest_payment: Record<string, any> | null;
    active_hold: Record<string, any> | null;
    tasks: Array<Record<string, any>>;
    recent_events: Array<Record<string, any>>;
    renewal_history: Array<Record<string, any>>;
  };
}

export async function getDmpLifecycleAccounts(): Promise<DmpLifecycleAccount[]> {
  const response = await http.get<{ data: DmpLifecycleAccount[] }>("/api/clients/dmp-lifecycle");
  return response.data?.data ?? [];
}

export async function getDmpLifecycleAccount(clientId: string | number) {
  const response = await http.get<{ data: DmpLifecycleAccount }>(`/api/clients/${clientId}/dmp-lifecycle`);
  return response.data?.data ?? null;
}

export async function recordDmpPayment(clientId: string | number, payload: {
  payment_received_on: string;
  months_purchased: number;
  plan_name?: string;
  payment_reference?: string;
  approval_evidence: string;
}) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/payments`, payload)).data;
}

export async function reverseDmpPayment(clientId: string | number, termId: number, reason: string) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/payments/${termId}/reverse`, { reason })).data;
}

export async function completeDmpOnboarding(clientId: string | number, payload: {
  service_start_on?: string;
  note?: string;
}) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/onboarding`, payload)).data;
}

export async function adjustDmpSubscriptionDates(clientId: string | number, payload: {
  start_on: string;
  months_purchased: number;
  reason: string;
}) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/date-adjustments`, payload)).data;
}

export async function createDmpAccountHold(clientId: string | number, payload: {
  reason: string;
  starts_on?: string;
  ends_on?: string;
  review_on?: string;
  owner_label?: string;
  affected_services?: string[];
}) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/holds`, payload)).data;
}

export async function releaseDmpAccountHold(clientId: string | number, holdId: number, reason: string) {
  return (await http.patch(`/api/clients/${clientId}/dmp-lifecycle/holds/${holdId}/release`, { reason })).data;
}

export async function updateDmpLifecycleTask(clientId: string | number, taskId: number, payload: {
  checklist_key: string;
  completed: boolean;
  resumption_on?: string;
  note?: string;
}) {
  return (await http.patch(`/api/clients/${clientId}/dmp-lifecycle/tasks/${taskId}`, payload)).data;
}

export async function acknowledgeDmpLifecycleTask(clientId: string | number, taskId: number) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/tasks/${taskId}/acknowledge`)).data;
}

export async function recordDmpRenewalContact(clientId: string | number, taskId: number, payload: {
  contacted_at: string;
  method: "phone" | "email" | "message" | "other";
  outcome: string;
  next_followup_on?: string;
}) {
  return (await http.post(`/api/clients/${clientId}/dmp-lifecycle/tasks/${taskId}/contacts`, payload)).data;
}

export interface GetClientsParams {
  scope?: string;
  signal?: AbortSignal;
  lightweight?: boolean;
}

export const getClients = async (params?: GetClientsParams) => {
  const scope = typeof params?.scope === "string" ? params.scope : undefined;
  const requestParams: Record<string, string> = {};

  if (scope) {
    requestParams.scope = scope;
  }

  if (params?.lightweight) {
    requestParams.lightweight = "1";
  }

  const response = await http.get("/api/clients", {
    params: Object.keys(requestParams).length > 0 ? requestParams : undefined,
    signal: params?.signal,
  });

  return response.data;
};

export const getClientById = async (clientId: string | number) => {
  const clientReference = encodeURIComponent(String(clientId));
  const response = await http.get(`/api/clients/${clientReference}`);
  return response.data?.data ?? null;
};

export const createClient = async (payload: CreateClientPayload) => {
  const response = await http.post("/api/clients", payload);
  return response.data?.data ?? response.data;
};

export const updateClient = async (
  clientId: string | number,
  payload: UpdateClientPayload
) => {
  const hasFileUpload = payload.dmp_plan_file instanceof File;
  const response = hasFileUpload
    ? await httpMultipart.post(
        `/api/clients/${clientId}`,
        buildUpdateClientFormData(payload)
      )
    : await http.put(`/api/clients/${clientId}`, payload);
  return response.data?.data ?? response.data;
};

export const getClientCredentialAccessCode = async (
  clientId: string | number
) => {
  const response = await http.get(
    `/api/clients/${encodeURIComponent(String(clientId))}/credential-access-code`
  );

  return response.data?.data ?? null;
};

function buildUpdateClientFormData(payload: UpdateClientPayload) {
  const formData = new FormData();

  formData.append("_method", "PUT");
  if (payload.company_name != null) {
    formData.append("company_name", payload.company_name);
  }

  if ("email" in payload) {
    formData.append("email", payload.email ?? "");
  }
  if ("website_url" in payload) {
    formData.append("website_url", payload.website_url ?? "");
  }
  if ("password" in payload) {
    formData.append("password", payload.password ?? "");
  }
  if ("send_login_credentials" in payload) {
    formData.append(
      "send_login_credentials",
      payload.send_login_credentials ? "1" : "0"
    );
  }
  if ("login_access_code" in payload) {
    formData.append("login_access_code", payload.login_access_code ?? "");
  }
  if ("activation_date" in payload) {
    formData.append("activation_date", payload.activation_date ?? "");
  }
  if ("onboarding_date" in payload) {
    formData.append("onboarding_date", payload.onboarding_date ?? "");
  }
  if ("subscription_start_date" in payload) {
    formData.append(
      "subscription_start_date",
      payload.subscription_start_date ?? ""
    );
  }
  if ("renewal_date" in payload) {
    formData.append("renewal_date", payload.renewal_date ?? "");
  }
  if ("renewal_start_date" in payload) {
    formData.append("renewal_start_date", payload.renewal_start_date ?? "");
  }
  if ("renewal_end_date" in payload) {
    formData.append("renewal_end_date", payload.renewal_end_date ?? "");
  }
  if ("payment_due_date" in payload) {
    formData.append("payment_due_date", payload.payment_due_date ?? "");
  }
  if ("date_renewed" in payload) {
    formData.append("date_renewed", payload.date_renewed ?? "");
  }
  if ("months_renewed" in payload && payload.months_renewed != null) {
    formData.append("months_renewed", String(payload.months_renewed));
  }
  if ("renewal_status" in payload) {
    formData.append("renewal_status", payload.renewal_status ?? "");
  }
  if ("renewal_note" in payload) {
    formData.append("renewal_note", payload.renewal_note ?? "");
  }
  if ("assigned_user_id" in payload) {
    formData.append("assigned_user_id", payload.assigned_user_id ?? "");
  }
  if (Array.isArray(payload.assigned_member_ids)) {
    payload.assigned_member_ids.forEach((memberId) => {
      formData.append("assigned_member_ids[]", memberId);
    });
  }
  if ("package_id" in payload && payload.package_id != null) {
    formData.append("package_id", String(payload.package_id));
  }
  if ("client_account_status" in payload && payload.client_account_status) {
    formData.append("client_account_status", payload.client_account_status);
  }
  if ("offboarding_tasks_complete" in payload) {
    formData.append(
      "offboarding_tasks_complete",
      payload.offboarding_tasks_complete ? "1" : "0"
    );
  }
  if ("activation_plan_type" in payload) {
    formData.append("activation_plan_type", payload.activation_plan_type ?? "");
  }
  if (
    "activation_custom_package_id" in payload &&
    payload.activation_custom_package_id != null
  ) {
    formData.append(
      "activation_custom_package_id",
      String(payload.activation_custom_package_id)
    );
  }
  if ("activation_primary_goal" in payload) {
    formData.append("activation_primary_goal", payload.activation_primary_goal ?? "");
  }
  if ("activation_secondary_goal" in payload) {
    formData.append(
      "activation_secondary_goal",
      payload.activation_secondary_goal ?? ""
    );
  }
  if (Array.isArray(payload.activation_custom_goals)) {
    payload.activation_custom_goals.forEach((goal) => {
      formData.append("activation_custom_goals[]", goal);
    });
  }
  if ("activation_notes" in payload) {
    formData.append("activation_notes", payload.activation_notes ?? "");
  }
  if (Array.isArray(payload.service_ids)) {
    payload.service_ids.forEach((serviceId) => {
      formData.append("service_ids[]", String(serviceId));
    });
  }
  if ("service_start_date" in payload) {
    formData.append("service_start_date", payload.service_start_date ?? "");
  }
  if ("service_end_date" in payload) {
    formData.append("service_end_date", payload.service_end_date ?? "");
  }
  if (payload.dmp_plan_file instanceof File) {
    formData.append("dmp_plan_file", payload.dmp_plan_file);
  }

  return formData;
}

export const archiveClient = async (clientId: string | number) => {
  const response = await http.delete(`/api/clients/${clientId}`);
  return response.data;
};

export interface WhatsappNotificationSettings {
  whatsapp_notifications_enabled: boolean;
  whatsapp_number: string | null;
}

export const updateClientWhatsappSettings = async (
  clientId: string | number,
  payload: WhatsappNotificationSettings
) => {
  const response = await http.put(
    `/api/clients/${encodeURIComponent(String(clientId))}/whatsapp-settings`,
    payload
  );

  return (response.data?.data ?? response.data) as WhatsappNotificationSettings;
};

export const syncDmpClients = async () => {
  const response = await http.post<SyncDmpClientsResponse>("/api/clients/sync-dmp");
  return response.data;
};
